import { extractErrorInfo } from './errorInfo';

const RELOAD_STORAGE_KEY_PREFIX = 'portal:stale-asset-reload';

const STALE_ASSET_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'unable to preload css for',
];

interface ReloadStaleAssetOnceOptions {
  buildId?: string;
  reload?: () => void;
  storage?: Storage | null;
}

function getBuildId(): string {
  return (
    import.meta.env?.VITE_GIT_SHA || import.meta.env?.MODE || 'unknown-build'
  );
}

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function isStaleAssetLoadError(error: unknown): boolean {
  const searchText = extractErrorInfo(error, { defaultMessage: '' }).searchText;

  return STALE_ASSET_ERROR_PATTERNS.some((pattern) =>
    searchText.includes(pattern),
  );
}

export function reloadStaleAssetOnce(
  error: unknown,
  options: ReloadStaleAssetOnceOptions = {},
): boolean {
  if (!isStaleAssetLoadError(error)) {
    return false;
  }

  const storage = options.storage ?? getSessionStorage();
  const storageKey = `${RELOAD_STORAGE_KEY_PREFIX}:${
    options.buildId ?? getBuildId()
  }`;

  if (storage?.getItem(storageKey)) {
    return false;
  }

  try {
    storage?.setItem(storageKey, String(Date.now()));
  } catch {
    // If storage is unavailable, still try one recovery reload.
  }

  const reload =
    options.reload ??
    (() => {
      window.location.reload();
    });
  reload();

  return true;
}

export function installStaleAssetReloadHandler(): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handleVitePreloadError = (event: Event) => {
    const preloadEvent = event as Event & { payload?: unknown };
    if (reloadStaleAssetOnce(preloadEvent.payload ?? event)) {
      event.preventDefault();
    }
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (reloadStaleAssetOnce(event.reason)) {
      event.preventDefault();
    }
  };

  const handleError = (event: ErrorEvent) => {
    void reloadStaleAssetOnce(event.error ?? event.message);
  };

  window.addEventListener('vite:preloadError', handleVitePreloadError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
  window.addEventListener('error', handleError);

  return () => {
    window.removeEventListener('vite:preloadError', handleVitePreloadError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    window.removeEventListener('error', handleError);
  };
}
